import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Avatar,
  Collapse,
  Link,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ListIcon from '@mui/icons-material/List';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import FeedbackButtons from './FeedbackButtons';

const getFavicon = (url) => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
  } catch {
    return "";
  }
};

export const handleCopy = (text) => {
  navigator.clipboard.writeText(text).catch((err) => {
    console.error("Failed to copy: ", err);
  });
};

const CopyButton = ({ text, tooltip = "Inhalt kopieren", ariaLabel = "copy" }) => {
  return (
    <Tooltip title={tooltip} placement="top">
      <IconButton size="small" onClick={() => handleCopy(text)} aria-label={ariaLabel}>
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const ExpandableText = ({ text, maxLength = 500 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text.length <= maxLength) {
    return <span>{text}</span>;
  }

  const truncatedText = text.substring(0, maxLength);
  const remainingText = text.substring(maxLength);

  const dotStyle = {
    cursor: 'pointer',
    fontSize: '1.0rem',
    fontWeight: 600,
    color: 'grey',
    marginLeft: '4px',
  };

  const collapseStyle = {
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: 'grey',
    marginLeft: '8px',
    textDecoration: 'underline',
  };

  return (
    <span>
      {truncatedText}
      {isExpanded ? (
        <>
          {remainingText}
          <span style={collapseStyle} onClick={() => setIsExpanded(false)}>
            einklappen
          </span>
        </>
      ) : (
        <span style={dotStyle} onClick={() => setIsExpanded(true)}>
          ...
        </span>
      )}
    </span>
  );
};

function safeParseJSON(input) {
  if (typeof input === 'object') {
    return input; // Already parsed
  }

  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch (e1) {
      try {
        const fixed = input
          // Replace double backslash-n with actual \n
          .replace(/\\\\n/g, '\\n')
          // Fix single-quoted keys and values
          .replace(/([{,])\s*'([^']+?)'\s*:/g, '$1"$2":')
          .replace(/:\s*'([^']*?)'\s*(?=[},])/g, ':"$1"')
          .replace(/^'/, '"').replace(/'$/, '"')
          .replace(/\\xa0\d*/g, ' ');  // Replace \xa0 or \xa01 etc. with space

        return JSON.parse(fixed);
      } catch (e2) {
        console.warn('Failed to parse input as JSON:', e2);
        return null;
      }
    }
  }

  return null;
}

function formatDate(input) {
    const date = new Date(input);
    if (isNaN(date)) return "";

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // months are 0-based
    const year = date.getUTCFullYear();

    return `${day}.${month}.${year}`;
}

const ResultCard = ({ item, onFeedback, resultIndex }) => {
    const [feedback, setFeedback] = useState("none");

    const handleFeedback = (type) => {
        setFeedback(type);
        if (onFeedback) {
            onFeedback(item, type, resultIndex);
        }
    };
    const numberOfVerweisboxes = 5;
    let type = item.metadata.type || "Unbekannt";
    if (type === "Decision") {
        // States
        const [showPrev, setShowPrev] = useState(false);
        const [showNext, setShowNext] = useState(false);
        const [showSummary, setShowSummary] = useState(false);
        const [showLeitsaetze, setShowLeitsaetze] = useState(false);
        const [showExpandedThemen, setShowExpandedThemen] = useState(false);
        const [showExpandedNormen, setShowExpandedNormen] = useState(false);

        // Values
        type = "Entscheidung";
        const country = item.metadata.country || "Land";
        const entscheidungsArt = item.metadata?.decision_type ? `(${item.metadata.decision_type})` : '';
        const courtTranslated = item.metadata.court_translated || "Gericht";
        const decisionDate = item.metadata.decision_date || "Datum";
        const decisionDateFormatted = formatDate(decisionDate);
        const caseNr = item.metadata.case_nr || "Aktenzeichen";
        const quellenZitat = `${courtTranslated} ${decisionDateFormatted}, ${caseNr} ${entscheidungsArt}`;
        const url = item.metadata.url || "#";
        const headline = item.metadata.headline || "Überschrift";
        const textPrev = item.metadata.chunks?.[0] || "";
        // Uncomment back if items are sent as array of chunks
        // const text = item.metadata.chunks?.[1] || "";
        const text = item.metadata.orig_text || "Text";
        const textNext = item.metadata.chunks?.[2] || "";
        const summary = item.metadata.summary && item.metadata.summary.trim() ? item.metadata.summary : null;
        const aiSummary = item.metadata.ai_summary || false;
        let relatedLeitsaetze = item.metadata.related_leitsaetze || [];
        relatedLeitsaetze = [...relatedLeitsaetze].sort((a, b) => a.leitsatz_nr - b.leitsatz_nr);
        const favicon = getFavicon(url);
        const themen = item.metadata.themen || [];
        const normen = item.metadata.normen || [];
        const dotStyle = {
            cursor: 'pointer',
            fontSize: '1.2rem',
            fontWeight: 600,
            color: 'grey',
        };

        return (
            <Card sx={{ mb: 2, px: 2, py: 1, position: 'relative' }}>
                <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                        <Avatar src={favicon} variant="square" sx={{ width: 24, height: 24, mt: '2px' }} />
                        <Box display="flex" flexDirection="column">
                            <Tooltip title="Klicken zum Kopieren"  placement="top-start" enterDelay={300} leaveDelay={200}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleCopy(quellenZitat)}
                            >
                                {quellenZitat}
                            </Typography>
                            </Tooltip>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            <Box component="span" fontWeight="bold">{type}</Box> -{" "}
                            <Tooltip title={url}>
                                <Link href={url} target="_blank" rel="noopener" 
                                sx={{
                                    display: 'inline-block',
                                    maxWidth: 300,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'bottom'
                                }}
                                >
                                {url}
                                </Link>
                            </Tooltip>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Headline*/}
                    <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{headline}</Typography>
                    <Tooltip title="Entscheidungsabschnitt kopieren" placement="top">
                        <IconButton
                        size="small"
                        onClick={() => handleCopy(`${headline}\n\n${textPrev} ${text} ${textNext}\n(${quellenZitat})`)}
                        aria-label="Entscheidungsabschnitt kopieren"
                        >
                        <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    </Box>

                    {/* Text display */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                    <Typography variant="body1">
                        {/* Previous text inline */}
                        {textPrev && (
                        <>
                            {showPrev ? (
                            <span style={{ color: 'gray', cursor: 'pointer' }} onClick={() => setShowPrev(false)}>
                                {textPrev}
                            </span>
                            ) : (
                            <span style={dotStyle} onClick={() => setShowPrev(true)}>
                                ...{' '}
                            </span>
                            )}
                        </>
                        )}

                        {/* Main text with expandable functionality */}
                        <ExpandableText text={text} maxLength={500} />

                        {/* Next text inline */}
                        {textNext && (
                        <>
                            {showNext ? (
                            <span style={{ color: 'gray', cursor: 'pointer' }} onClick={() => setShowNext(false)}>
                                {textNext}
                            </span>
                            ) : (
                            <span style={dotStyle} onClick={() => setShowNext(true)}>
                                {' '}
                                ...
                            </span>
                            )}
                        </>
                        )}
                    </Typography>
                    </Box>

                    {/* Summary and Leitsätze Buttons */}
                    {(summary || relatedLeitsaetze.length > 0) && (
                    <Box>
                        {/* Buttons */}
                        <Box display="flex" gap={1} mb={1} mt={1}>
                        {summary && (
                            <Button
                            size="medium"
                            startIcon={<DescriptionOutlinedIcon />}
                            onClick={() => {
                                setShowSummary((prev) => {
                                const next = !prev;
                                setShowLeitsaetze(false);
                                return next;
                                });
                            }}
                            sx={{ textTransform: 'none' }}
                            >
                            Zusammenfassung
                            </Button>
                        )}

                        {relatedLeitsaetze.length > 0 && (
                            <Button
                            size="medium"
                            startIcon={<ListIcon />}
                            onClick={() => {
                                setShowLeitsaetze((prev) => {
                                const next = !prev;
                                setShowSummary(false);
                                return next;
                                });
                            }}
                            sx={{ textTransform: 'none' }}
                            >
                            Leitsätze
                            </Button>
                        )}
                        </Box>

                        {/* Shared Copy Button */}
                        {(showSummary || showLeitsaetze) && (
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                            <CopyButton tooltip="Inhalt kopieren" ariaLabel="Inhalt kopieren"
                                text={ showSummary ? summary + `\n(${quellenZitat})`: relatedLeitsaetze.map((lt) => `• ${lt.text}`).join('\n') + `\n(${quellenZitat})`}
                            />
                            </Box>
                        </Box>
                        )}

                        {/* Summary */}
                        <Collapse in={showSummary} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            >
                            {aiSummary ? (
                                <>
                                <AutoAwesomeIcon fontSize="small" />
                                KI generierte Zusammenfassung der Entscheidung:
                                </>
                            ) : (
                                <>
                                <FactCheckIcon fontSize="small" />
                                Redaktionell erstellte Zusammenfassung der Entscheidung:
                                </>
                            )}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                            <ReactMarkdown>{summary}</ReactMarkdown>
                            </Typography>
                        </Box>
                        </Collapse>

                        {/* Leitsätze */}
                        <Collapse in={showLeitsaetze} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            >
                            <ListIcon fontSize="small" />
                            Zugehörige Leitsätze:
                            </Typography>

                            <Box sx={{ mt: 1 }}>
                            {relatedLeitsaetze.map((lt, index) => (
                                <Typography key={index} variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                • <ExpandableText text={lt.text} maxLength={300} />
                                </Typography>
                            ))}
                            </Box>
                        </Box>
                        </Collapse>
                    </Box>
                    )}
                                        
                </CardContent>
                {(themen.length > 0 || normen.length > 0) && (
                <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                    {themen.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {themen.slice(0, showExpandedThemen ? themen.length : Math.min(numberOfVerweisboxes, themen.length)).map((thema, index) => (
                        <Tooltip title={thema} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color:"text.secondary",
                                backgroundColor: 'rgba(85, 160, 94, 0.6)',
                                fontSize: '0.75rem'}}>
                                {thema}
                            </Typography>
                        </Tooltip>
                        ))}
                        {themen.length > 10 && (
                        <Tooltip title={showExpandedThemen ? "Weniger anzeigen" : `${themen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedThemen(!showExpandedThemen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(85, 160, 94, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedThemen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                    </Box>
                    )}

                    {normen.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {normen.slice(0, showExpandedNormen ? normen.length : Math.min(numberOfVerweisboxes, normen.length)).map((norm, index) => (
                        <Tooltip title={norm} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color:"text.secondary",
                                backgroundColor: 'rgba(120, 113, 202, 0.6)',
                                fontSize: '0.75rem'}}>
                                {norm}
                            </Typography>
                        </Tooltip>
                        ))}
                        {normen.length > 10 && (
                        <Tooltip title={showExpandedNormen ? "Weniger anzeigen" : `${normen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedNormen(!showExpandedNormen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(120, 113, 202, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedNormen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                    </Box>                    )}
                </Box>
                )}

                <Box px={2} pb={1}>
                    <FeedbackButtons feedback={feedback} onFeedback={handleFeedback} />
                </Box>
            </Card>
        );

    } else if (type === "Leitsatz") {
        // States
        const [showSummary, setShowSummary] = useState(false);
        const [showLeitsaetze, setShowLeitsaetze] = useState(false);
        const [showExpandedThemen, setShowExpandedThemen] = useState(false);
        const [showExpandedNormen, setShowExpandedNormen] = useState(false);

        // Values
        const courtTranslated = item.metadata.court_translated || "Gericht";
        const entscheidungsArt = item.metadata?.decision_type ? `(${item.metadata.decision_type})` : '';
        const decisionDate = item.metadata.decision_date || "Datum";
        const decisionDateFormatted = formatDate(decisionDate);
        const caseNr = item.metadata.case_nr || "Aktenzeichen";
        const quellenZitat = `${courtTranslated} ${decisionDateFormatted}, ${caseNr} ${entscheidungsArt}`;
        const url = item.metadata.url || "#";
        const headline = item.metadata.headline || "Überschrift";
        const text = item.metadata.leitsatz || "Leitsatztext";
        const summary = item.metadata.summary && item.metadata.summary.trim() ? item.metadata.summary : null;
        const aiSummary = item.metadata.ai_summary || false;
        let relatedLeitsaetze = item.metadata.related_leitsaetze || [];
        relatedLeitsaetze = [...relatedLeitsaetze].sort((a, b) => a.leitsatz_nr - b.leitsatz_nr);
        const favicon = getFavicon(url);
        const themen = item.metadata.themen || [];
        const normen = item.metadata.normen || [];

        return (
            <Card sx={{ mb: 2, px: 2, py: 1, position: 'relative' }}>
                <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                        <Avatar src={favicon} variant="square" sx={{ width: 24, height: 24, mt: '2px' }} />
                        <Box display="flex" flexDirection="column">
                            <Tooltip title="Klicken zum Kopieren"  placement="top-start" enterDelay={300} leaveDelay={200}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleCopy(quellenZitat)}
                            >
                                {quellenZitat}
                            </Typography>
                            </Tooltip>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            <Box component="span" fontWeight="bold">{type}</Box> -{" "}
                            <Tooltip title={url}>
                                <Link href={url} target="_blank" rel="noopener" 
                                sx={{
                                    display: 'inline-block',
                                    maxWidth: 300,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'bottom'
                                }}
                                >
                                {url}
                                </Link>
                            </Tooltip>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Headline*/}
                    <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{headline}</Typography>
                    <Tooltip title="Leitsatz kopieren" placement="top">
                        <IconButton
                        size="small"
                        onClick={() => handleCopy(`${headline}\n\n${text}\n(${quellenZitat})`)}
                        aria-label="Leitsatz kopieren"
                        >
                        <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    </Box>

                    {/* Text display */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                        <Typography variant="body1">
                            <ExpandableText text={text} maxLength={500} />
                        </Typography>
                    </Box>

                    {/* Summary and Leitsätze Buttons */}
                    {(summary || relatedLeitsaetze.length > 0) && (
                    <Box>
                        {/* Buttons */}
                        <Box display="flex" gap={1} mb={1} mt={1}>
                        {summary && (
                            <Button
                            size="medium"
                            startIcon={<DescriptionOutlinedIcon />}
                            onClick={() => {
                                setShowSummary((prev) => {
                                const next = !prev;
                                setShowLeitsaetze(false);
                                return next;
                                });
                            }}
                            sx={{ textTransform: 'none' }}
                            >
                            Zusammenfassung
                            </Button>
                        )}

                        {relatedLeitsaetze.length > 0 && (
                            <Button
                            size="medium"
                            startIcon={<ListIcon />}
                            onClick={() => {
                                setShowLeitsaetze((prev) => {
                                const next = !prev;
                                setShowSummary(false);
                                return next;
                                });
                            }}
                            sx={{ textTransform: 'none' }}
                            >
                            Weitere Leitsätze der Entscheidung
                            </Button>
                        )}
                        </Box>

                        {/* Shared Copy Button */}
                        {(showSummary || showLeitsaetze) && (
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                            <CopyButton tooltip="Inhalt kopieren" ariaLabel="Inhalt kopieren"
                                text={ showSummary ? summary + `\n(${quellenZitat})`: relatedLeitsaetze.map((lt) => `• ${lt.text}`).join('\n') + `\n(${quellenZitat})`}
                            />
                            </Box>
                        </Box>
                        )}

                        {/* Summary */}
                        <Collapse in={showSummary} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            >
                            {aiSummary ? (
                                <>
                                <AutoAwesomeIcon fontSize="small" />
                                KI generierte Zusammenfassung der Entscheidung:
                                </>
                            ) : (
                                <>
                                <FactCheckIcon fontSize="small" />
                                Redaktionell erstellte Zusammenfassung der Entscheidung:
                                </>
                            )}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                            <ReactMarkdown>{summary}</ReactMarkdown>
                            </Typography>
                        </Box>
                        </Collapse>

                        {/* Leitsätze */}
                        <Collapse in={showLeitsaetze} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                            fontWeight="bold"
                            display="flex"
                            alignItems="center"
                            gap={1}
                            >
                            <ListIcon fontSize="small" />
                            Zugehörige Leitsätze:
                            </Typography>

                            <Box sx={{ mt: 1 }}>
                            {relatedLeitsaetze.map((lt, index) => (
                                <Typography key={index} variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                • <ExpandableText text={lt.text} maxLength={300} />
                                </Typography>
                            ))}
                            </Box>
                        </Box>
                        </Collapse>
                    </Box>
                    )}
                                        
                </CardContent>
                {(themen.length > 0 || normen.length > 0) && (
                <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                    {themen.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {themen.slice(0, showExpandedThemen ? themen.length : Math.min(numberOfVerweisboxes, themen.length)).map((thema, index) => (
                        <Tooltip title={thema} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color:"text.secondary",
                                backgroundColor: 'rgba(85, 160, 94, 0.6)',
                                fontSize: '0.75rem'}}>
                                {thema}
                            </Typography>
                        </Tooltip>
                        ))}
                        {themen.length > 10 && (
                        <Tooltip title={showExpandedThemen ? "Weniger anzeigen" : `${themen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedThemen(!showExpandedThemen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(85, 160, 94, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedThemen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                    </Box>
                    )}

                    {normen.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {normen.slice(0, showExpandedNormen ? normen.length : Math.min(numberOfVerweisboxes, normen.length)).map((norm, index) => (
                        <Tooltip title={norm} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color:"text.secondary",
                                backgroundColor: 'rgba(120, 113, 202, 0.6)',
                                fontSize: '0.75rem'}}>
                                {norm}
                            </Typography>
                        </Tooltip>
                        ))}
                        {normen.length > 10 && (
                        <Tooltip title={showExpandedNormen ? "Weniger anzeigen" : `${normen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedNormen(!showExpandedNormen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(120, 113, 202, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedNormen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                    </Box>
                    )}
                </Box>
                )}
                <Box px={2} pb={1}>
                    <FeedbackButtons feedback={feedback} onFeedback={handleFeedback} />
                </Box>
            </Card>
        );
    } else if (type === "Norm") {
        // States
        const [showErwg, setShowErwg] = useState(false);
        const [showExpandedThemen, setShowExpandedThemen] = useState(false);

        // Values
        const normName = item.metadata.name || "Norm Name";
        const quellenZitat = `${normName}`; // 1.
        const url = item.metadata.url || "#";
        const headline = item.metadata.headline || "Überschrift";
        const text = item.metadata.norm_text || "Normtext";
        let relatedErwg = safeParseJSON(item.metadata.related_erwaegungsgruende) || [];
        relatedErwg = [...relatedErwg].sort((a, b) => a.erwg - b.erwg);
        const favicon = getFavicon(url);
        const themen = safeParseJSON(item.metadata.themen) || [];

        return (
            <Card sx={{ mb: 2, px: 2, py: 1, position: 'relative' }}>
                <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                        <Avatar src={favicon} variant="square" sx={{ width: 24, height: 24, mt: '2px' }} />
                        <Box display="flex" flexDirection="column">
                            <Tooltip title="Klicken zum Kopieren"  placement="top-start" enterDelay={300} leaveDelay={200}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleCopy(quellenZitat)}
                            >
                                {quellenZitat}
                            </Typography>
                            </Tooltip>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            <Box component="span" fontWeight="bold">{type}</Box> -{" "}
                            <Tooltip title={url}>
                                <Link href={url} target="_blank" rel="noopener" 
                                sx={{
                                    display: 'inline-block',
                                    maxWidth: 300,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'bottom'
                                }}
                                >
                                {url}
                                </Link>
                            </Tooltip>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Headline*/}
                    <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{headline}</Typography>
                    <Tooltip title="Normtext kopieren" placement="top">
                        <IconButton
                        size="small"
                        onClick={() => handleCopy(`${headline}\n\n${text}\n(${quellenZitat})`)}
                        aria-label="Normtext kopieren"
                        >
                        <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    </Box>

                    {/* Text display */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                    <Typography variant="body1">
                        <ExpandableText text={text} maxLength={500} />
                    </Typography>
                    </Box>

                    {/* Erwägungen Section */}
                    {relatedErwg.length > 0 && (
                    <Box>
                        {/* Button */}
                        <Box display="flex" gap={1} mb={1} mt={1}>
                        <Button
                            size="medium"
                            startIcon={<MenuBookOutlinedIcon />}
                            onClick={() => setShowErwg((prev) => !prev)}
                            sx={{ textTransform: 'none' }}
                        >
                            Weitere Erwägungsgründe der Norm
                        </Button>
                        </Box>

                        {/* Copy Button */}
                        {showErwg && (
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                            <CopyButton tooltip="Inhalt kopieren" ariaLabel="Inhalt kopieren"
                                text={relatedErwg.map((lt) => `${lt.name}: ${lt.erwg_text}`).join('\n') + `\n(${quellenZitat})`}
                            />
                            </Box>                            
                        </Box>
                        )}

                        {/* Erwägungen Collapse */}
                        <Collapse in={showErwg} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>
                            <Box sx={{ mt: 1 }}>
                            {relatedErwg.map((lt, index) => (
                                <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                component="div"
                                sx={{ mt: 1 }}
                                >
                                <strong>{lt.name}</strong>: <ExpandableText text={lt.erwg_text} maxLength={300} />
                                </Typography>
                            ))}
                            </Box>
                        </Box>
                        </Collapse>
                    </Box>
                    )}

                    </CardContent>

                    {/* Themen only */}
                    {themen.length > 0 && (
                    <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {themen.slice(0, showExpandedThemen ? themen.length : Math.min(numberOfVerweisboxes, themen.length)).map((thema, index) => (
                            <Tooltip title={thema} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color: 'text.secondary',
                                backgroundColor: 'rgba(85, 160, 94, 0.6)',
                                fontSize: '0.75rem',
                                }}
                            >
                                {thema}
                            </Typography>
                            </Tooltip>
                        ))}
                        {themen.length > 10 && (
                        <Tooltip title={showExpandedThemen ? "Weniger anzeigen" : `${themen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedThemen(!showExpandedThemen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(85, 160, 94, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedThemen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                        </Box>
                    </Box>
                    )}
                <Box px={2} pb={1}>
                    <FeedbackButtons feedback={feedback} onFeedback={handleFeedback} />
                </Box>
            </Card>
        );        
    } else if (type === "Erwaegungsgrund") {
        // States
        const [showNormen, setShowNormen] = useState(false);
        const [showExpandedThemen, setShowExpandedThemen] = useState(false);
        
        // Values
        let type = "Erwägungsgrund";
        const erwgName = item.metadata.name || "Erwg Name";
        const quellenZitat = `${erwgName}`;
        const url = item.metadata.url || "#";
        const headline = item.metadata.headline || "Überschrift";
        const text = item.metadata.erwg_text || "Normtext";
        let relatedNormen = safeParseJSON(item.metadata.related_normen) || [];

        // relatedNormen = [...relatedNormen].sort((a, b) => a.norm_text - b.norm_text);
        const favicon = getFavicon(url);
        const themen = safeParseJSON(item.metadata.themen) || [];

        return (
            <Card sx={{ mb: 2, px: 2, py: 1, position: 'relative' }}>
                <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                        <Avatar src={favicon} variant="square" sx={{ width: 24, height: 24, mt: '2px' }} />
                        <Box display="flex" flexDirection="column">
                            <Tooltip title="Klicken zum Kopieren"  placement="top-start" enterDelay={300} leaveDelay={200}>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleCopy(quellenZitat)}
                            >
                                {quellenZitat}
                            </Typography>
                            </Tooltip>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                            <Box component="span" fontWeight="bold">{type}</Box> -{" "}
                            <Tooltip title={url}>
                                <Link href={url} target="_blank" rel="noopener" 
                                sx={{
                                    display: 'inline-block',
                                    maxWidth: 300,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'bottom'
                                }}
                                >
                                {url}
                                </Link>
                            </Tooltip>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Headline*/}
                    <Box mt={1} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{headline}</Typography>
                    <Tooltip title="Erwägungsgrund kopieren" placement="top">
                        <IconButton
                        size="small"
                        onClick={() => handleCopy(`${headline}\n\n${text}\n(${quellenZitat})`)}
                        aria-label="Erwägungsgrund kopieren"
                        >
                        <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    </Box>

                    {/* Text display */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                    <Typography variant="body1">
                        <ExpandableText text={text} maxLength={500} />
                    </Typography>
                    </Box>

                    {/* Normen Section */}
                    {relatedNormen.length > 0 && (
                    <Box>
                        {/* Button */}
                        <Box display="flex" gap={1} mb={1} mt={1}>
                        <Button
                            size="medium"
                            startIcon={<FormatQuoteOutlinedIcon />}
                            onClick={() => setShowNormen((prev) => !prev)}
                            sx={{ textTransform: 'none' }}
                        >
                            Normverweise
                        </Button>
                        </Box>

                        {/* Copy Button */}
                        {showNormen && (
                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                            <CopyButton tooltip="Inhalt kopieren" ariaLabel="Inhalt kopieren"
                                text={ relatedNormen.map((lt) => `${lt.name}: ${lt.norm_text}`).join('\n') + `\n(${quellenZitat})`}
                            />
                            </Box>
                        </Box>
                        )}

                        {/* Normen Collapse */}
                        <Collapse in={showNormen} timeout="auto" unmountOnExit>
                        <Box sx={{ position: 'relative', mt: 1 }}>

                            <Box sx={{ mt: 1 }}>
                            {relatedNormen.map((lt, index) => (
                                <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                component="div"
                                sx={{ mt: 1 }}
                                >
                                <strong>{lt.name}</strong>: <ExpandableText text={lt.norm_text} maxLength={300} />
                                </Typography>
                            ))}
                            </Box>
                        </Box>
                        </Collapse>
                    </Box>
                    )}

                    </CardContent>

                    {/* Themen only */}
                    {themen.length > 0 && (
                    <Box px={2} pb={2} display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {themen.slice(0, showExpandedThemen ? themen.length : Math.min(numberOfVerweisboxes, themen.length)).map((thema, index) => (
                            <Tooltip title={thema} key={index}>
                            <Typography
                                variant="body2"
                                sx={{
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                color: 'text.secondary',
                                backgroundColor: 'rgba(85, 160, 94, 0.6)',
                                fontSize: '0.75rem',
                                }}
                            >
                                {thema}
                            </Typography>
                            </Tooltip>
                        ))}
                        {themen.length > 10 && (
                        <Tooltip title={showExpandedThemen ? "Weniger anzeigen" : `${themen.length - numberOfVerweisboxes} weitere anzeigen`}>
                            <IconButton
                                size="small"
                                onClick={() => setShowExpandedThemen(!showExpandedThemen)}
                                sx={{ 
                                    width: 24, 
                                    height: 24,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(85, 160, 94, 0.1)'
                                    }
                                }}
                            >
                                {showExpandedThemen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        )}
                        </Box>
                    </Box>
                    )}
                <Box px={2} pb={1}>
                    <FeedbackButtons feedback={feedback} onFeedback={handleFeedback} />
                </Box>
            </Card>
        );
    } else{
        // Fallback for unknown types
        return (
            <Card sx={{ mb: 2, px: 2, py: 1 }}>
                <CardContent>
                    <Typography variant="body1" color="text.secondary">
                        Unbekannter Inhaltstyp: {type}
                    </Typography>
                </CardContent>
            </Card>
        );
    }
};

export default ResultCard; 

